import React, { useState, FC, PropsWithChildren, useEffect } from "react"
import { Outlet } from "react-router-dom"

import { collection, doc, FirestoreError, onSnapshot, query, where } from "firebase/firestore"

import { GroupDocument, GroupsContext, OrganisationDocument } from "."
import groupConverter from "./utils/groupConverter"
import { firebase } from "../../services/firebase/firebase"
import { useAuth } from "../authentication-context"
import useContextMap from "../utils/useContextMap"
import organisationConverter from "./utils/organisationConverter"

type GroupsProviderProps = {
    route?: boolean
}

export const GroupsProvider: FC<PropsWithChildren<GroupsProviderProps>> = ({ children, route = false }) => {
    const [data, setData] = useState<GroupDocument[]>([])
    const [organisation, setOrganisation] = useState<OrganisationDocument | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<FirestoreError>()
    const map = useContextMap(data)
    const { user, isAuth } = useAuth()

    useEffect(() => {
        if (user && isAuth) {
            if (user.groups !== true && user.groups.length === 0) return

            const ref = collection(firebase.firestore, `organisations/${user.organisation}/groups`).withConverter(
                groupConverter,
            )
            return onSnapshot(
                user.groups === true ? ref : query(ref, where("__name__", "in", user.groups)),
                groups => {
                    setData(groups.docs.map(doc => doc.data()))
                    setLoading(false)
                },
                setError,
            )
        }
    }, [user, isAuth])

    useEffect(() => {
        if (isAuth)
            return onSnapshot(
                doc(firebase.firestore, `organisations/${user?.organisation}`).withConverter(organisationConverter),
                organisation => {
                    setOrganisation(organisation.data() ?? null)
                    setLoading(false)
                },
                setError,
            )
    }, [user, isAuth])

    useEffect(() => {
        if (error) console.error()
    }, [error])

    return (
        <GroupsContext.Provider value={{ data, map, organisation, loading, error }}>
            {route ? <Outlet /> : children}
        </GroupsContext.Provider>
    )
}
